import React, { useState, useEffect } from "react";
import { collection, query, getDocs, doc, updateDoc, deleteDoc, setDoc, addDoc, getDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, deleteUser, getAuth, updatePassword } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { Plus, Loader2, AlertCircle, Shield, User, Mail, Key, Trash2, CreditCard as Edit, Check, X, UserPlus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import UserExcelImport from './UserExcelImport';
import { useThemeStore } from '../stores/themeStore';
import { useUserStore } from '../stores/userStore';

interface UserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  lastLogin?: Date;
  createdAt: Date;
  photoURL?: string;
  mfaEnabled?: boolean;
  isAdmin?: boolean;
  uid?: string; // Firebase Auth UID
}

export default function UserManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user'
  });
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    role: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { darkMode } = useThemeStore();
  const { userData, isAdmin } = useUserStore();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const usersData: UserData[] = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Skip deleted users
        if (data.deleted) return;
        
        usersData.push({
          id: doc.id,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role || 'user',
          lastLogin: data.lastLogin?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          photoURL: data.photoURL,
          mfaEnabled: data.mfaEnabled,
          isAdmin: data.isAdmin,
          uid: data.uid // Store Firebase Auth UID
        });
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Check admin permissions
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour créer un utilisateur');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Validation
      if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName) {
        throw new Error('Tous les champs sont requis');
      }
      
      if (newUser.password.length < 8) {
        throw new Error('Le mot de passe doit contenir au moins 8 caractères');
      }
      
      // Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );
      
      const uid = userCredential.user.uid;
      
      // Créer le document utilisateur dans Firestore
      await setDoc(doc(db, 'users', uid), {
        uid: uid,
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        createdAt: new Date(),
        updatedAt: new Date(),
        initialPassword: newUser.password,
        passwordSetByAdmin: true
      });
      
      // Ajouter l'utilisateur à l'état local
      setUsers(prev => [...prev, {
        id: uid,
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        createdAt: new Date(),
        uid: uid
      }]);
      
      // Réinitialiser le formulaire
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'user'
      });
      
      setShowAddUser(false);
      toast.success('Utilisateur créé avec succès');
    } catch (error) {
      console.error('Error adding user:', error);
      setError('Erreur lors de la création de l\'utilisateur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async (userId: string) => {
    if (isSubmitting) return;
    
    // Check admin permissions
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour modifier un utilisateur');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Validation
      if (!editData.firstName || !editData.lastName) {
        throw new Error('Le nom et le prénom sont requis');
      }
      
      // Mettre à jour le document utilisateur dans Firestore
      await updateDoc(doc(db, 'users', userId), {
        firstName: editData.firstName,
        lastName: editData.lastName,
        role: editData.role,
        updatedAt: new Date()
      });
      
      // Mettre à jour l'état local
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, firstName: editData.firstName, lastName: editData.lastName, role: editData.role }
          : user
      ));
      
      setEditingUser(null);
      toast.success('Utilisateur mis à jour avec succès');
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    // Check admin permissions first
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour supprimer un utilisateur');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      // Get the user document to find the Firebase Auth UID
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('Utilisateur non trouvé');
      }
      
      const userData = userDoc.data();
      const firebaseUid = userData.uid;
      
      if (!firebaseUid) {
        throw new Error('UID Firebase non trouvé pour cet utilisateur');
      }
      
      // Delete the user from Firebase Auth if possible
      try {
        // This requires a Cloud Function or Admin SDK in a real environment
        // For now, we'll just mark the user as deleted in Firestore
        console.log('Would delete Firebase Auth user with UID:', firebaseUid);
        
        // In a real implementation, you would call a Cloud Function:
        // await functions().httpsCallable('deleteUser')({ uid: firebaseUid });
      } catch (authError) {
        console.error('Error deleting user from Firebase Auth:', authError);
        // Continue with Firestore deletion even if Auth deletion fails
      }
      
      // Mark the user as deleted in Firestore
      await updateDoc(doc(db, 'users', userId), {
        deleted: true,
        deletedAt: new Date()
      });
      
      // Update the local state
      setUsers(prev => prev.filter(user => user.id !== userId));
      
      toast.success('Utilisateur supprimé avec succès');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression de l\'utilisateur: ' + 
                 ('Erreur inconnue'));
    }
  };

  const handleResetPassword = async (userId: string, email: string) => {
    // Check admin permissions
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour réinitialiser un mot de passe');
      return;
    }

    const newPassword = prompt('Entrez le nouveau mot de passe (minimum 8 caractères):');
    
    if (!newPassword) return;
    
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    try {
      // Mettre à jour le mot de passe dans Firestore
      await updateDoc(doc(db, 'users', userId), {
        initialPassword: newPassword,
        passwordSetByAdmin: true,
        passwordSetAt: new Date()
      });
      
      toast.success('Mot de passe réinitialisé avec succès');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleImportUsers = async (users: any[]) => {
    // Check admin permissions
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour importer des utilisateurs');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const user of users) {
        try {
          // Créer l'utilisateur dans Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            user.email,
            user.password
          );
          
          const uid = userCredential.user.uid;
          
          // Créer le document utilisateur dans Firestore
          await setDoc(doc(db, 'users', uid), {
            uid: uid,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            createdAt: new Date(),
            updatedAt: new Date(),
            initialPassword: user.password,
            passwordSetByAdmin: true
          });
          
          successCount++;
        } catch (error) {
          console.error(`Error importing user ${user.email}:`, error);
          errorCount++;
        }
      }
      
      if (errorCount === 0) {
        toast.success(`${successCount} utilisateur(s) importé(s) avec succès`);
      } else {
        toast.success(`${successCount} utilisateur(s) importé(s) avec succès, ${errorCount} erreur(s)`);
      }
      
      setShowImport(false);
      loadUsers();
    } catch (error) {
      console.error('Error importing users:', error);
      setError('Erreur lors de l\'import des utilisateurs');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (user: UserData) => {
    // Check admin permissions
    if (!isAdmin) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour modifier un utilisateur');
      return;
    }

    setEditingUser(user.id);
    setEditData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      user.role.toLowerCase().includes(searchLower)
    );
  });

  const getUserInitials = (user: UserData): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    
    return 'U';
  };

  // Show access denied message if user is not admin
  if (!isAdmin && auth.currentUser?.email !== 'slucas@anthea-rh.com') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className={`${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-700'} rounded-xl p-6 text-center max-w-lg`}>
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-red-300' : 'text-red-700'} mb-2`}>Accès refusé</h2>
          <p className={darkMode ? 'text-red-300' : 'text-red-600'}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page. 
            Seuls les administrateurs peuvent gérer les utilisateurs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Gestion des utilisateurs</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setShowImport(true)}
            className={`hidden md:flex items-center px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Import Excel
          </button>
          <button
            onClick={() => setShowAddUser(true)}
            className="flex items-center px-4 py-2 bg-gradient-anthea text-white rounded-2xl hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
          />
        </div>
      </div>

      {error && (
        <div className={`p-4 ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg flex items-start gap-2 mb-6`}>
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Utilisateur
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Rôle
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Dernière connexion
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    MFA
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}`}>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {getUserInitials(user)}
                        </div>
                        <div className="ml-4">
                          {editingUser === user.id ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editData.firstName}
                                onChange={(e) => setEditData({...editData, firstName: e.target.value})}
                                placeholder="Prénom"
                                className={`block w-full px-2 py-1 text-sm rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                              />
                              <input
                                type="text"
                                value={editData.lastName}
                                onChange={(e) => setEditData({...editData, lastName: e.target.value})}
                                placeholder="Nom"
                                className={`block w-full px-2 py-1 text-sm rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                              />
                            </div>
                          ) : (
                            <>
                              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {user.firstName} {user.lastName}
                              </div>
                              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.email}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingUser === user.id ? (
                        <select
                          value={editData.role}
                          onChange={(e) => setEditData({...editData, role: e.target.value})}
                          className={`block w-full px-2 py-1 text-sm rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        >
                          <option value="user">Utilisateur</option>
                          <option value="manager">Manager</option>
                          <option value="analyst">Analyste</option>
                          <option value="admin">Administrateur</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === 'admin' 
                            ? darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                            : user.role === 'manager'
                            ? darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                            : user.role === 'analyst'
                            ? darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                            : darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          {user.role === 'admin' ? 'Administrateur' : 
                           user.role === 'manager' ? 'Manager' : 
                           user.role === 'analyst' ? 'Analyste' : 
                           'Utilisateur'}
                        </span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.mfaEnabled 
                          ? darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                          : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.mfaEnabled ? 'Activé' : 'Désactivé'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingUser === user.id ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user.id)}
                            className={`p-1 ${darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'}`}
                          >
                            <Check className="w-5 h-5" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className={`p-1 ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditing(user)}
                            className={`p-1 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                            title="Modifier"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user.id, user.email)}
                            className={`p-1 ${darkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-yellow-600 hover:text-yellow-700'}`}
                            title="Réinitialiser le mot de passe"
                          >
                            <Key className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className={`p-1 ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                            title="Supprimer"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal d'ajout d'utilisateur */}
      {showAddUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-md`}>
            <form onSubmit={handleAddUser}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Nouvel utilisateur</h2>
                  <button 
                    type="button" 
                    onClick={() => setShowAddUser(false)} 
                    className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Email
                    </label>
                    <div className="relative">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className={`w-full pl-10 pr-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Mot de passe
                    </label>
                    <div className="relative">
                      <Key className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className={`w-full pl-10 pr-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                        required
                        minLength={8}
                      />
                    </div>
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Minimum 8 caractères
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={newUser.firstName}
                        onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                        className={`w-full px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                        Nom
                      </label>
                      <input
                        type="text"
                        value={newUser.lastName}
                        onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                        className={`w-full px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Rôle
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className={`w-full px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                    >
                      <option value="user">Utilisateur</option>
                      <option value="manager">Manager</option>
                      <option value="analyst">Analyste</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-b-xl flex justify-end gap-4`}>
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className={`px-4 py-2 ${
                    darkMode 
                      ? 'text-gray-300 hover:bg-gray-600' 
                      : 'text-gray-700 hover:bg-gray-100'
                  } rounded-lg`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gradient-anthea text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Créer l'utilisateur
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'import Excel */}
      {showImport && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-2xl`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Importer des utilisateurs</h2>
                <button 
                  onClick={() => setShowImport(false)} 
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <UserExcelImport 
                onImport={handleImportUsers}
                onClose={() => setShowImport(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}